import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Button,
  Table,
  Tag,
  Space,
  Spin,
  Empty,
  message,
  Modal,
  List,
  Avatar
} from 'antd';
import {
  CalendarOutlined,
  DollarOutlined,
  HomeOutlined,
  PlusOutlined,
  EyeOutlined,
  TeamOutlined,
  HeartOutlined,
  GiftOutlined,
  SmileOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, attendanceAPI, paymentsAPI, centersAPI } from '../../utils/api';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const { Title, Text } = Typography;

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [wellnessCenters, setWellnessCenters] = useState([]);
  const [centersLoading, setCentersLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, 'days').toDate(),
    moment().toDate()
  ]);
  const [celebrations, setCelebrations] = useState([]);
  const [celebrationModalVisible, setCelebrationModalVisible] = useState(false);
  const [sendingWishes, setSendingWishes] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
    if (user?.role === 'super_admin') {
      fetchWellnessCenters();
    } else {
      fetchCelebrations();
    }
  }, [user]);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      fetchRecentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      message.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentData = async () => {
    try {
      if (!dateRange[0] || !dateRange[1]) return;

      // Fetch recent attendance
      const attendanceResponse = await attendanceAPI.getAttendance({
        limit: 10,
        start_date: moment(dateRange[0]).format('YYYY-MM-DD'),
        end_date: moment(dateRange[1]).format('YYYY-MM-DD')
      });
      setRecentAttendance(attendanceResponse.data.attendance || []);

      // Fetch recent payments
      const paymentsResponse = await paymentsAPI.getPayments({
        limit: 10,
        start_date: moment(dateRange[0]).format('YYYY-MM-DD'),
        end_date: moment(dateRange[1]).format('YYYY-MM-DD')
      });
      setRecentPayments(paymentsResponse.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch recent data:', error);
    }
  };

  const fetchWellnessCenters = async () => {
    try {
      setCentersLoading(true);
      const response = await centersAPI.getCenters();
      setWellnessCenters(response.data.centers || []);
    } catch (error) {
      console.error('Failed to fetch wellness centers:', error);
    } finally {
      setCentersLoading(false);
    }
  };

  const fetchCelebrations = async () => {
    try {
      console.log('🎉 Fetching celebrations from frontend...');
      const response = await adminAPI.getCelebrations();
      console.log('📊 Celebrations response:', response.data);
      const celebrationsList = response.data.celebrations || [];
      console.log('🎂 Celebrations list:', celebrationsList);
      setCelebrations(celebrationsList);
      if (celebrationsList.length > 0) {
        console.log('✅ Opening celebration modal!');
        setCelebrationModalVisible(true);
      } else {
        console.log('ℹ️ No celebrations today');
      }
    } catch (error) {
      console.error('❌ Failed to fetch celebrations:', error);
    }
  };

  const attendanceColumns = [
    {
      title: 'Consumer',
      dataIndex: 'consumer_name',
      key: 'consumer_name',
      render: (text, record) => (
        <Link to={`/consumers/${record.consumer_id}`}>
          {text}
        </Link>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Check In',
      dataIndex: 'check_in_time',
      key: 'check_in_time',
      render: (time) => time ? moment(time).format('HH:mm') : '-',
    },
    // {
    //   title: 'Check Out',
    //   dataIndex: 'check_out_time',
    //   key: 'check_out_time',
    //   render: (time) => time ? moment(time).format('HH:mm') : '-',
    // },
    {
      title: 'Duration',
      dataIndex: 'hours_spent',
      key: 'hours_spent',
      render: (hours) => hours ? `${Math.round(hours * 100) / 100}h` : '-',
    },
  ];

  const paymentsColumns = [
    {
      title: 'Consumer',
      dataIndex: 'consumer_name',
      key: 'consumer_name',
      render: (text, record) => (
        <Link to={`/consumers/${record.consumer_id}`}>
          {text}
        </Link>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `₹${amount}`,
    },
    {
      title: 'Type',
      dataIndex: 'payment_type',
      key: 'payment_type',
      render: (type) => (
        <Tag color={type === 'monthly' ? 'blue' : 'green'}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'completed' ? 'success' :
          status === 'pending' ? 'warning' : 'error';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
  ];

  const centersColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (address) => address || '-',
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.email || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'Consumers',
      dataIndex: 'consumer_count',
      key: 'consumer_count',
      render: (count) => count || 0,
    },
    {
      title: 'Admins',
      dataIndex: 'admin_count',
      key: 'admin_count',
      render: (count) => count || 0,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderCelebrationIcon = (type) => {
    return type === 'birthday' ? <SmileOutlined style={{ fontSize: '40px', color: '#ff4d4f' }} /> : <GiftOutlined style={{ fontSize: '40px', color: '#1890ff' }} />;
  };

  return (
    <div>
      {/* Celebration Modal */}
      <Modal
        title={
          <span style={{ fontSize: '20px' }}>
            <GiftOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
            Today's Celebrations! 🎉
          </span>
        }
        open={celebrationModalVisible}
        onCancel={() => setCelebrationModalVisible(false)}
        footer={[
          <Button
            key="send"
            type="primary"
            icon={<GiftOutlined />}
            onClick={async () => {
              try {
                setSendingWishes(true);
                const response = await adminAPI.sendCelebrationEmails();
                const { sent, failed, errors } = response.data;

                if (failed > 0) {
                  message.warning(`${sent} sent, ${failed} failed. Check console for details.`);
                  console.error('Failed emails:', errors);
                } else {
                  message.success(`All ${sent} wishes sent successfully!`);
                }
              } catch (error) {
                message.error('Failed to send wishes');
              } finally {
                setSendingWishes(false);
              }
            }}
            loading={sendingWishes}
            style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', marginRight: 8 }}
          >
            Send Wishes
          </Button>,
          <Button key="close" onClick={() => setCelebrationModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <List
          itemLayout="horizontal"
          dataSource={celebrations}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <Avatar
                    size={64}
                    icon={renderCelebrationIcon(item.celebration_type)}
                    style={{
                      backgroundColor: item.celebration_type === 'birthday' ? '#fff1f0' : '#e6f7ff',
                      border: `2px solid ${item.celebration_type === 'birthday' ? '#ff4d4f' : '#1890ff'}`
                    }}
                  />
                }
                title={
                  <span style={{ fontSize: '16px', fontWeight: 500 }}>
                    {item.name}
                    {item.celebration_type === 'birthday' && (
                      <Tag color="red" style={{ marginLeft: '8px' }}>
                        🎂 {item.years} years
                      </Tag>
                    )}
                    {item.celebration_type === 'anniversary' && (
                      <Tag color="blue" style={{ marginLeft: '8px' }}>
                        💍 {item.years} years
                      </Tag>
                    )}
                  </span>
                }
                description={
                  <div>
                    <div style={{ marginTop: '4px' }}>
                      {item.celebration_type === 'birthday' ? '🎈 Happy Birthday!' : '💐 Happy Anniversary!'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      {item.email} • {item.phone}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
      {/* Welcome Section */}
      <Card className="welcome-card" style={{ marginBottom: 24 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={16}>
            <Title level={2} style={{ color: 'white', marginBottom: 8 }}>
              Welcome back, {user?.name}! 👋
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '16px' }}>
              {user?.role === 'super_admin'
                ? 'Manage all wellness centers and their operations from here.'
                : `Here's what's happening at ${user?.center_name || 'your wellness center'} today.`}
            </Text>
          </Col>
          {user?.role !== 'super_admin' && (
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
              <Space direction="vertical" size="middle">
                <Button
                  icon={<GiftOutlined />}
                  size="large"
                  onClick={() => {
                    fetchCelebrations();
                    message.info('Checking for celebrations...');
                  }}
                >
                  Check Celebrations
                </Button>
                <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                  {moment().format('MMMM DD, YYYY')}
                </Text>
              </Space>
            </Col>
          )}
          {user?.role === 'super_admin' && (
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
              <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                {moment().format('MMMM DD, YYYY')}
              </Text>
            </Col>
          )}
        </Row>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[24, 24]} className="dashboard-stats" style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Consumers"
              value={dashboardData?.consumers?.total || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ({dashboardData?.consumers?.active || 0} active)
                </Text>
              }
            />
          </Card>
        </Col>
        {user?.role !== 'super_admin' && (
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Today's Attendance"
                value={dashboardData?.attendance?.today || 0}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        )}
        {user?.role !== 'super_admin' && (
          <>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="This Month's Payments"
                  value={dashboardData?.payments?.this_month || 0}
                  prefix="₹"
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Monthly Revenue"
                  value={dashboardData?.payments?.amount_this_month || 0}
                  prefix="₹"
                  precision={2}
                  valueStyle={{ color: '#eb2f96' }}
                />
              </Card>
            </Col>
          </>
        )}
        {user?.role === 'super_admin' && (
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Wellness Centers"
                value={dashboardData?.wellness_centers || 0}
                prefix={<HomeOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        )}
      </Row>

      {/* Quick Actions - Only for Center Admins */}
      {
        user?.role !== 'super_admin' && (
          <Card
            title="Center Management Actions"
            style={{ marginBottom: 24 }}
          >
            <Space wrap>
              {/* <Button type="primary" icon={<PlusOutlined />}>
                <Link to="/consumers/new">Add Consumer</Link>
              </Button> */}
              <Button icon={<TeamOutlined />}>
                <Link to="/consumers">Manage Consumers</Link>
              </Button>
              <Button icon={<CalendarOutlined />}>
                <Link to="/attendance">View Attendance</Link>
              </Button>
              <Button icon={<HeartOutlined />}>
                <Link to="/measurements">Health Records</Link>
              </Button>
              <Button icon={<DollarOutlined />}>
                <Link to="/payments">Payment History</Link>
              </Button>
            </Space>
          </Card>
        )
      }

      {/* Wellness Centers - Only for Super Admins */}
      {
        user?.role === 'super_admin' && (
          <Card
            title="Wellness Centers"
            extra={
              <Link to="/centers">
                <Button type="link" icon={<EyeOutlined />} size="small">
                  View All
                </Button>
              </Link>
            }
          >
            {centersLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
              </div>
            ) : wellnessCenters.length > 0 ? (
              <Table
                columns={centersColumns}
                dataSource={wellnessCenters}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                size="small"
                scroll={{ x: 800 }}
              />
            ) : (
              <Empty description="No wellness centers found" />
            )}
          </Card>
        )
      }

      {/* Recent Activity - Only for Center Admins */}
      {
        user?.role !== 'super_admin' && (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card
                title="Recent Attendance"
                extra={
                  <Space>
                    <DatePicker
                      selected={dateRange[0]}
                      onChange={(dates) => {
                        if (Array.isArray(dates)) {
                          // Range selected
                          if (dates[0] && dates[1]) {
                            setDateRange(dates);
                          } else if (dates[0]) {
                            // Only start date selected
                            setDateRange([dates[0], dateRange[1] || dates[0]]);
                          }
                        } else if (dates) {
                          // Single date clicked (start of new range)
                          setDateRange([dates, null]);
                        } else {
                          // Cleared
                          setDateRange([moment().subtract(7, 'days').toDate(), moment().toDate()]);
                        }
                      }}
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      selectsRange
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select date range"
                      className="ant-input"
                      style={{ width: 240, height: 32 }}
                    />
                    <Link to="/attendance">
                      <Button type="link" icon={<EyeOutlined />} size="small">
                        View All
                      </Button>
                    </Link>
                  </Space>
                }
              >
                {recentAttendance.length > 0 ? (
                  <Table
                    columns={attendanceColumns}
                    dataSource={recentAttendance}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <Empty description="No recent attendance records" />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                title="Recent Payments"
                extra={
                  <Link to="/payments">
                    <Button type="link" icon={<EyeOutlined />} size="small">
                      View All
                    </Button>
                  </Link>
                }
              >
                {recentPayments.length > 0 ? (
                  <Table
                    columns={paymentsColumns}
                    dataSource={recentPayments}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <Empty description="No recent payment records" />
                )}
              </Card>
            </Col>
          </Row>
        )
      }
    </div >
  );
};

export default Dashboard;
